import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GroupLogin from './GroupLogin';
import MapWithResizableFilters from './MapWithResizableFilters';
import AIFinder from './AIFinder';
import WeatherAlerts from './WeatherAlerts';
import EventsCalendar from './EventsCalendar';
import { useEvents } from '../hooks/useEvents';
import { useIsMobile, useIsTablet } from '../hooks/useMediaQuery';
import '../styles/MapResponsive.css';
import { getFieldLabel } from '../utils/labels.js';
import { useRegionFilters } from '../hooks/useRegionFilters';
import LandscapeWarning from './LandscapeWarning';
import LocationButton from './LocationButton';
import { useNavigate, useParams } from 'react-router-dom';
import { useGroupContext } from '../hooks/useGroupContext';
import { useGroup } from '../contexts/GroupContext.tsx';
import ContactModal from './ContactModal';

let summariesCache = null;

async function loadSummaries() {
  if (summariesCache !== null) return summariesCache;
  try {
    const response = await fetch('/data/summaries.json');
    if (!response.ok) {
      console.warn('⚠️ summaries.json non disponibile');
      summariesCache = {};
      return summariesCache;
    }
    summariesCache = await response.json();
    console.log(`✅ Caricate ${Object.keys(summariesCache).length} summaries`);
    return summariesCache;
  } catch (error) {
    console.error('❌ Errore caricamento summaries.json:', error);
    summariesCache = {};
    return summariesCache;
  }
}

function ScrollingText({ children, speed = 15 }) {
  return (
    <div style={{ overflow: 'hidden', position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
      <div style={{ display: 'inline-block', whiteSpace: 'nowrap', animation: `scroll-horizontal ${speed}s linear infinite`, paddingRight: '50px' }}>
        {children}
      </div>
      <style>{`@keyframes scroll-horizontal { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }`}</style>
    </div>
  );
}

/**
 * @param {{ nameRegion: string, folder: string, contentType: string, groupId?: string, focusTrailId?: string, focusPoiId?: string }} props
 */
export default function ResponsiveMapWrapper({
  nameRegion, folder, contentType, groupId: groupIdProp,
  focusTrailId = undefined,
  focusPoiId = undefined
}) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;
  const mode = isSmallScreen ? 'mobile' : 'desktop';
  const isActivityMode = folder === 'activity';
  const isCampania = nameRegion?.toLowerCase() === 'campania';
  const { t } = useTranslation();

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [weatherModalOpen, setWeatherModalOpen] = useState(false);
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [databaseReady, setDatabaseReady] = useState(false);
  const [regionData, setRegionData] = useState(null);
  const [selectedTrail, setSelectedTrail] = useState(null);
  const { groupId: groupIdFromUrl } = useParams();
  const { groupId, isPersonal } = useGroupContext(groupIdProp || groupIdFromUrl);
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const { events } = useEvents(nameRegion);
  const { groupName } = useGroup();

  const {
    filteredFeatures: centralFilteredFeatures,
    mappedFeatures: centralMappedFeatures,
    dynamicFilters,
    selectedDynamic,
    filtersHash,
    toggleDynamicFilter,
    resetFilters,
    getDifficultyLabel
  } = useRegionFilters(isActivityMode ? null : regionData);
  const [contactOpen, setContactOpen] = useState(false);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [activityData, setActivityData] = useState(null);

  const [localFocusTrailId] = useState(() => {
    if (!focusTrailId) return null;
    const key = `focus_trail_consumed_${focusTrailId}`;
    if (sessionStorage.getItem(key)) return null;
    sessionStorage.setItem(key, '1');
    return focusTrailId;
  });

  const [localFocusPoiId] = useState(() => {
    if (!focusPoiId) return null;
    const key = `focus_poi_consumed_${focusPoiId}`;
    if (sessionStorage.getItem(key)) return null;
    sessionStorage.setItem(key, '1');
    return focusPoiId;
  });

  const trailFocusDone = useRef(false);
  const poiFocusDone = useRef(false);

  const effectiveFilteredFeatures = useMemo(() =>
    isActivityMode ? (regionData?.features || []) : centralFilteredFeatures,
    [isActivityMode, regionData?.features, centralFilteredFeatures]
  );
  const effectiveMappedFeatures = useMemo(() =>
    isActivityMode ? (regionData?.features || []) : centralMappedFeatures,
    [isActivityMode, regionData?.features, centralMappedFeatures]
  );

  useEffect(() => {
    if (!nameRegion) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const mainResponse = await fetch(`/data/${folder}/${nameRegion.toLowerCase()}.json`);
        const mainData = await mainResponse.json();
        setRegionData(mainData);
        console.log(`✅ Dati ${folder} caricati per ${nameRegion}`);

        if (folder === 'hiking') {
          try {
            const activityResponse = await fetch(`/data/activity/${nameRegion.toLowerCase()}.json`);
            if (activityResponse.ok) {
              const actData = await activityResponse.json();
              setActivityData(actData);
              console.log(`✅ Dati activity caricati per ${nameRegion} (${actData.features?.length || 0} POI)`);
            } else {
              setActivityData(null);
            }
          } catch {
            setActivityData(null);
          }
        } else {
          setActivityData(null);
        }
        setLoading(false);
      } catch (error) {
        console.error("Errore caricamento regione:", error);
        setLoading(false);
      }

      if (!isActivityMode) {
        try {
          const loadRes = await fetch(`${process.env.REACT_APP_API_URL_NODE}/api/load-region`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ nameRegion })
          });
          const loadData = await loadRes.json();
          if (loadData.success && loadData.loaded) {
            console.log(`✅ Database vettoriale caricato per ${nameRegion}`);
            setDatabaseReady(true);
          } else {
            console.error(`❌ Database NON caricato: ${loadData.message}`);
          }
        } catch (loadErr) {
          console.error('❌ Errore caricamento database:', loadErr);
        }
      } else {
        setDatabaseReady(false);
      }
    };
    fetchData();
  }, [nameRegion, folder, isActivityMode]);

  const [summaries, setSummaries] = useState(null);
  useEffect(() => {
    loadSummaries().then(data => setSummaries(data));
  }, []);

  useEffect(() => {
    if (!localFocusTrailId || loading || !regionData || trailFocusDone.current) return;
    const timer = setTimeout(() => {
      if (window.focusTrail) {
        console.log('🎯 Focus trail:', localFocusTrailId);
        window.focusTrail(localFocusTrailId);
        trailFocusDone.current = true;
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [localFocusTrailId, loading, regionData]);

  useEffect(() => {
    if (!localFocusPoiId || loading || !regionData || poiFocusDone.current) return;
    const timer = setTimeout(() => {
      if (window.focusPoi) {
        console.log('🎯 Focus POI:', localFocusPoiId);
        window.focusPoi(localFocusPoiId);
        poiFocusDone.current = true;
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [localFocusPoiId, loading, regionData]);

  const handleTrailSelectForWeather = (trail) => setSelectedTrail(trail);

  const handleOpenWeather = () => {
    if (!selectedTrail) {
      alert(t('select_trail_first'));
      return;
    }
    setWeatherModalOpen(true);
  };

  const handleOpenWeatherForTrail = (trail) => {
    if (!trail) return;
    if (!trail.geometry || !trail.geometry.coordinates || trail.geometry.coordinates.length === 0) {
      alert(t('no_trail_coordinates'));
      return;
    }
    const trailName = trail.properties?.name || trail.name || 'Evento';
    setSelectedTrail({ ...trail, properties: { ...trail.properties, name: trailName } });
    setWeatherModalOpen(true);
  };

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientY);
  const handleTouchMove = (e) => {
    if (!touchStart) return;
    const diff = touchStart - e.touches[0].clientY;
    if (diff > 50) { setBottomSheetOpen(true); setTouchStart(null); }
    else if (diff < -50) { setBottomSheetOpen(false); setTouchStart(null); }
  };
  const handleTouchEnd = () => setTouchStart(null);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px', color: '#666' }}>
        📄 {t('loading_region', { region: nameRegion })}
      </div>
    );
  }
  if (!regionData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px', color: '#666' }}>
        ❌ {t('no_region_data', { region: nameRegion })}
      </div>
    );
  }

  const mapProps = {
    nameRegion,
    regionData,
    regionDataActivity: activityData,
    onTrailSelect: handleTrailSelectForWeather,
    onWeatherClick: handleOpenWeatherForTrail,
    summaries,
    disableFilters: isActivityMode,
    onMapReady: (map) => mapRef.current = map,
    contentType,
    filteredFeatures: effectiveFilteredFeatures,
    mappedFeatures: effectiveMappedFeatures,
    dynamicFilters,
    selectedDynamic,
    filtersHash,
    toggleDynamicFilter,
    resetFilters,
    getDifficultyLabel,
    groupId,
    isPersonal,
    focusPoiId: localFocusPoiId,
  };

  // ── Banner Monti Alburni (solo per Campania) ─────────────────────────────
  const alburnisBanner = isCampania && (
    <div
      onClick={() => navigate('/region/campania/monti-alburni')}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '7px 16px',
        background: 'linear-gradient(90deg, #2d5a27 0%, #5a8a2a 100%)',
        color: 'white',
        cursor: 'pointer',
        userSelect: 'none',
        flexShrink: 0,
        zIndex: 1001,
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 600 }}>
        🏔️ {t('alburni_mountains')} — {t('alburni_subtitle')}
      </span>
      <span style={{
        fontSize: 13,
        padding: '3px 10px',
        borderRadius: 20,
        background: 'rgba(255,255,255,0.2)',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}>
        {t('explore', 'Esplora →')}
      </span>
    </div>
  );

  // ============================================
  // RENDER DESKTOP
  // ============================================

  if (!isSmallScreen) {
    return (
      <div className="map-wrapper">
        {alburnisBanner}
        <header style={{ width: '100%', overflow: 'hidden', position: 'relative', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f5f5f5', borderBottom: '2px solid #4CAF50', padding: '0 20px', margin: 0 }}>
          <div style={{ flex: 1, minWidth: 0, marginRight: '12px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <ScrollingText speed={18}>
              <span style={{ fontSize: '20px', fontWeight: '600', color: '#333' }}>
                {selectedTrail
                  ? `📍 ${selectedTrail.properties?.name || t('selected_trail', 'Percorso selezionato')}`
                  : (isActivityMode
                    ? t('region_poi_title', { region: nameRegion }, `📍 ${nameRegion} - Punti di Interesse`)
                    : groupName
                      ? t('group_ai_title', { group: groupName }, `👥 ${groupName} · 🎯 Trova il percorso perfetto con l'AI`)
                      : t('ai_title', '🎯 Trova il percorso perfetto – grazie all\'intelligenza artificiale')
                  )
                }
              </span>
            </ScrollingText>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <GroupLogin showCopyLink={folder === 'hiking'} className="desktop-group-login" />
            {!isActivityMode && selectedTrail && (
              <button onClick={handleOpenWeather} style={{ padding: '8px 16px', background: '#2196F3', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', minWidth: '44px', minHeight: '44px', flexShrink: 0 }}>
                🌤️ {t('weather')}
              </button>
            )}
            <button onClick={() => setAboutModalOpen(true)} style={{ padding: '8px 16px', background: 'white', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', minWidth: '44px', minHeight: '44px', flexShrink: 0 }}>
              ℹ️ About
            </button>
            <button onClick={() => navigate(isPersonal ? '/preferences' : `/trekkings/${groupId}/preferences`)} style={{ padding: '8px 16px', background: 'white', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', minWidth: '44px', minHeight: '44px', flexShrink: 0 }}>
              {isPersonal ? t('my_trails', '❤️ I miei Percorsi') : t('trail_preferences', '⭐ Preferenze Percorsi')}
            </button>
            <button className="menu-item" onClick={() => { setMenuDrawerOpen(false); setContactOpen(true); }}>
              ✉️ {t('contact_us', 'Contattaci')}
            </button>
          </div>
        </header>

        <div className="map-container" style={{ flex: 1 }}>
          <MapWithResizableFilters {...mapProps} mode="desktop" />
          <LocationButton mapRef={mapRef} nameRegion={nameRegion} navigate={navigate} isDesktop={true} />
        </div>

        {!isActivityMode && (
          <button className="ai-fab" onClick={() => setAiModalOpen(true)} title={t('search_with_ai')} aria-label={t('open_ai_search')}>🤖</button>
        )}
        {!isActivityMode && aiModalOpen && (
          <AIModal isOpen={aiModalOpen} onClose={() => setAiModalOpen(false)} databaseReady={databaseReady} filteredFeatures={effectiveFilteredFeatures} allFeatures={effectiveMappedFeatures} summaries={summaries} />
        )}
        {weatherModalOpen && selectedTrail && (
          <WeatherSidebarDesktop trail={selectedTrail} onClose={() => setWeatherModalOpen(false)} />
        )}
        {aboutModalOpen && <AboutModal onClose={() => setAboutModalOpen(false)} />}
        <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
      </div>
    );
  }

  // ============================================
  // RENDER MOBILE/TABLET
  // ============================================
  return (
    <div className="map-wrapper mobile-layout">
      {alburnisBanner}
      <header style={{ width: '100%', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', background: '#4CAF50', color: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', position: 'relative', zIndex: 1000 }}>
        <div style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
          <ScrollingText speed={12}>
            <span style={{ fontSize: selectedTrail ? '16px' : '20px', fontWeight: '600', color: 'white' }}>
              {selectedTrail
                ? `📍 ${selectedTrail.properties?.name || t('selected_trail')}`
                : (isActivityMode ? `📍 ${nameRegion} - ${t('points_of_interest')}` : t('ai_tagline'))}
            </span>
          </ScrollingText>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          {!isActivityMode && selectedTrail && (
            <button onClick={handleOpenWeather} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '20px', padding: '8px', borderRadius: '4px', cursor: 'pointer', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🌤️</button>
          )}
          <button onClick={() => setMenuDrawerOpen(true)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '24px', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', minWidth: '44px', minHeight: '44px' }} aria-label="Apri menu">☰</button>
        </div>
      </header>

      {menuDrawerOpen && (
        <>
          <div className="menu-drawer-overlay" onClick={() => setMenuDrawerOpen(false)} />
          <div className="menu-drawer">
            <div className="menu-drawer-header" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <h2 style={{ margin: 0 }}>Menu</h2>
              {/* GroupLogin in orizzontale, solo desktop */}
              <div style={{ flex: 1 }}>
                <GroupLogin showCopyLink={folder === 'hiking'} className="desktop-group-login" />
              </div>
              <button onClick={() => setMenuDrawerOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '8px', minWidth: '44px', minHeight: '44px', color: '#666' }} aria-label="Chiudi menu">✕</button>
            </div>
            <div className="menu-drawer-content">
              {isCampania && (
                <button className="menu-item" onClick={() => { setMenuDrawerOpen(false); navigate('/region/campania/monti-alburni'); }}>
                  🏔️ {t('alburni_mountains')}
                </button>
              )}
              {!isActivityMode && (<button className="menu-item" onClick={() => { setMenuDrawerOpen(false); setBottomSheetOpen(true); }}>🔍 {t('search_filters')}</button>)}
              {!isActivityMode && (<button className="menu-item" onClick={() => { setMenuDrawerOpen(false); setAiModalOpen(true); }}>🤖 {t('search_with_ai')}</button>)}
              <button className="menu-item" onClick={() => { setMenuDrawerOpen(false); setCalendarOpen(true); }}>
                🥾👥 {t('find_buddies')}
                {events.filter(e => e.eventType === 'trail_buddy').length > 0 && (
                  <span style={{ background: '#4CAF50', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', marginLeft: '8px' }}>
                    {events.filter(e => e.eventType === 'trail_buddy').length}
                  </span>
                )}
              </button>
              <button className="menu-item" onClick={() => { setMenuDrawerOpen(false); navigate(isPersonal ? '/preferences' : `/trekkings/${groupId}/preferences`); }}>
                {isPersonal ? t('my_trails', '❤️ I miei Percorsi') : t('trail_preferences', '⭐ Preferenze Percorsi')}
              </button>
              <button className="menu-item" onClick={() => { setMenuDrawerOpen(false); setAboutModalOpen(true); }}>ℹ️ About</button>
              <button className="menu-item" onClick={() => { setMenuDrawerOpen(false); setContactOpen(true); }}>
                ✉️ {t('contact_us', 'Contattaci')}
              </button>
            </div>
          </div>
        </>
      )}

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <MapWithResizableFilters {...mapProps} mode="mobile" />
      </div>

      {!isActivityMode && effectiveFilteredFeatures?.length > 0 && (
        <div style={{ position: 'absolute', top: '70px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '8px 16px', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 999, fontWeight: 'bold', fontSize: '14px' }}>
          {effectiveFilteredFeatures.length} {t('trails_label')}
        </div>
      )}
      {isActivityMode && regionData?.features && (
        <div style={{ position: 'absolute', top: '70px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '8px 16px', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 999, fontWeight: 'bold', fontSize: '14px' }}>
          {regionData.features.length} {t('points_label')}
        </div>
      )}

      {selectedTrail && (
        <div style={{ position: 'absolute', bottom: bottomSheetOpen ? 'calc(70vh + 20px)' : '80px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '12px', background: 'white', padding: '10px 16px', borderRadius: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', fontSize: '13px', fontWeight: '600', zIndex: 998, transition: 'bottom 0.3s ease', maxWidth: '90%', overflow: 'hidden' }}>
          {selectedTrail.properties?.distance && <span>📏 {(selectedTrail.properties.distance / 1000).toFixed(1)}km</span>}
          {selectedTrail.properties?.ascent && <span>⬆️ {selectedTrail.properties.ascent}m</span>}
          {selectedTrail.properties?.difficulty && <span>⚡ {selectedTrail.properties.difficulty}</span>}
        </div>
      )}

      {!isActivityMode && (
        <div
          className={`mobile-bottom-sheet ${bottomSheetOpen ? 'expanded' : 'collapsed'}`}
          style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTopLeftRadius: '16px', borderTopRightRadius: '16px', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', transition: 'transform 0.3s ease', transform: bottomSheetOpen ? 'translateY(0)' : 'translateY(calc(100% - 60px))', maxHeight: '70vh', zIndex: 1001, display: 'flex', flexDirection: 'column' }}
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
        >
          <div onClick={() => setBottomSheetOpen(!bottomSheetOpen)} style={{ width: '40px', height: '4px', background: '#ddd', borderRadius: '2px', margin: '12px auto', cursor: 'pointer' }} />
          <div style={{ padding: '0 16px 12px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{t('search_filters')}</h2>
            <button onClick={() => setBottomSheetOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '8px', minWidth: '44px', minHeight: '44px', color: '#666' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            <MobileFilters dynamicFilters={dynamicFilters} selectedDynamic={selectedDynamic} toggleDynamicFilter={toggleDynamicFilter} resetFilters={resetFilters} getDifficultyLabel={getDifficultyLabel} />
          </div>
        </div>
      )}

      <LocationButton mapRef={mapRef} nameRegion={nameRegion} navigate={navigate} isDesktop={false} />

      {!isActivityMode && (
        <button className={`ai-fab ${bottomSheetOpen ? 'fab-with-sheet-open' : ''}`} onClick={() => setAiModalOpen(true)} title={t('search_with_ai')} aria-label={t('open_ai_search')}>🤖</button>
      )}
      {!isActivityMode && aiModalOpen && (
        <AIModal isOpen={aiModalOpen} onClose={() => setAiModalOpen(false)} databaseReady={databaseReady} filteredFeatures={effectiveFilteredFeatures} allFeatures={effectiveMappedFeatures} summaries={summaries} />
      )}
      {calendarOpen && (
        <EventsCalendar region={nameRegion} onClose={() => setCalendarOpen(false)} />
      )}
      {weatherModalOpen && selectedTrail && (
        <WeatherModalMobile trail={selectedTrail} onClose={() => setWeatherModalOpen(false)} />
      )}
      {aboutModalOpen && <AboutModal onClose={() => setAboutModalOpen(false)} />}
      <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

// ============================================
// COMPONENTI HELPER
// ============================================

function AboutModal({ onClose }) {
  const isMobile = window.innerWidth <= 768;
  const { t } = useTranslation();
  return (
    <div className="about-modal-overlay" onClick={onClose}>
      <div className={isMobile ? "about-modal-mobile" : "about-modal-desktop"} onClick={(e) => e.stopPropagation()}>
        <div className="about-modal-header">
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '8px', marginRight: '12px', minWidth: '44px', minHeight: '44px', color: '#666' }} aria-label={t('close', 'Chiudi')}>{isMobile ? t('back', '←') : t('close', '✕')}</button>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', flex: 1 }}>{t('about_title', 'About')}</h2>
        </div>
        <div className="about-modal-content">
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h3 style={{ fontSize: '24px', margin: '0 0 8px 0', color: '#4CAF50' }}>{t('app_name', 'AI Hiking')}</h3>
            <p style={{ margin: 0, fontSize: '16px', color: '#666', fontStyle: 'italic' }}>{t('app_slogan', 'Ad ognuno la sua montagna')}</p>
          </div>
          <h4 style={{ fontSize: '18px', marginBottom: '20px', color: '#333' }}>{t('sources_used', 'Fonti utilizzate')}</h4>
          <div className="about-source">
            <strong style={{ display: 'block', marginBottom: '8px', color: '#2196F3' }}>📊 {t('weather_sources', 'Meteo e previsioni atmosferiche')}</strong>
            <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>{t('weather_sources_desc', 'Dati forniti da')} <a href="https://open-meteo.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none', fontWeight: '600' }}>Open-Meteo</a>, {t('weather_sources_note', 'servizio open-source basato su modelli meteorologici riconosciuti a livello internazionale.')}</p>
          </div>
          <div className="about-source">
            <strong style={{ display: 'block', marginBottom: '8px', color: '#FF9800' }}>⚠️ {t('avalanche_alerts', 'Allerte valanghe ufficiali')}</strong>
            <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>{t('avalanche_alerts_desc', 'Informazioni provenienti da')} <a href="https://www.aineva.it" target="_blank" rel="noopener noreferrer" style={{ color: '#FF9800', textDecoration: 'none', fontWeight: '600' }}>AINEVA</a>, {t('when_available', 'quando disponibili.')}</p>
          </div>
          <div className="about-source">
            <strong style={{ display: 'block', marginBottom: '8px', color: '#FF9800' }}>⚠️ {t('weather_alerts', 'Allerte meteo ufficiali')}</strong>
            <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>{t('weather_alerts_desc', 'Informazioni provenienti da')} <a href="https://meteoalarm.org" target="_blank" rel="noopener noreferrer" style={{ color: '#FF9800', textDecoration: 'none', fontWeight: '600' }}>METEOALARM</a>, {t('when_available', 'quando disponibili.')}</p>
          </div>
          <div className="about-source">
            <strong style={{ display: 'block', marginBottom: '8px', color: '#4CAF50' }}>🗺️ {t('geo_data', 'Dati geografici e sentieri')}</strong>
            <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>{t('geo_data_desc', 'Cartografia, percorsi e punti di interesse basati su')} <a href="https://www.openstreetmap.org" target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50', textDecoration: 'none', fontWeight: '600' }}>OpenStreetMap</a> {t('and_open_data', 'e open data pubblici.')}</p>
          </div>
          <div className="about-source">
            <strong style={{ display: 'block', marginBottom: '8px', color: '#9C27B0' }}>🤖 {t('ai_summaries', 'Sintesi e supporto informativo')}</strong>
            <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>{t('ai_summaries_desc', 'Alcune descrizioni e riepiloghi sono generati tramite modelli linguistici di OpenAI, senza valore di previsione o certificazione.')}</p>
          </div>
          <div className="about-source">
            <strong style={{ display: 'block', marginBottom: '8px', color: '#9C27B0' }}>🤖 {t('compass', 'La bussola')}</strong>
            <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>{t('compass_desc', 'La bussola visualizzata durante la navigazione ha solo scopo informativo. Per la sicurezza utilizzare strumenti idonei.')}</p>
          </div>
          <div style={{ marginTop: '32px', padding: '16px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#856404', lineHeight: '1.6' }}><strong>⚠️ {t('disclaimer', 'Disclaimer')}:</strong> {t('disclaimer_text', 'Le informazioni fornite hanno scopo puramente informativo. Verifica sempre le condizioni meteo aggiornate e le allerte ufficiali prima di intraprendere qualsiasi escursione in montagna.')}</p>
          </div>
          <div style={{ marginTop: '24px', textAlign: 'center', color: '#999', fontSize: '12px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
            <p style={{ margin: '4px 0' }}>{t('version', 'Versione 1.0.0')}</p>
            <p style={{ margin: '4px 0' }}>© 2025 {t('app_name', 'AI Hiking')}</p>
          </div>
          <div className="about-source">
            <strong style={{ display: 'block', marginBottom: '8px', color: '#4CAF50' }}>✉️ {t('contacts', 'Contatti')}</strong>
            <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>{t('contact_us_desc', 'Per qualsiasi motivo puoi scriverci a')} <a href="mailto:info@aihiking.eu" style={{ color: '#4CAF50', textDecoration: 'none', fontWeight: '600' }}>info@aihiking.eu</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AIModal({ isOpen, onClose, databaseReady, filteredFeatures, allFeatures, summaries }) {
  const { t } = useTranslation();
  const [isLandscape, setIsLandscape] = React.useState(window.innerWidth > window.innerHeight);
  React.useEffect(() => {
    const handleResize = () => setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => { window.removeEventListener('resize', handleResize); window.removeEventListener('orientationchange', handleResize); };
  }, []);
  if (!isOpen) return null;
  const isMobile = window.innerWidth <= 768;
  if (isMobile && isLandscape) return (<div className="ai-modal-overlay" onClick={onClose}><LandscapeWarning /></div>);
  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-modal-header">
          <button onClick={onClose} className="back-button" aria-label={t('close')}>←</button>
          <h2>{t('search_with_ai')}</h2>
        </div>
        {!databaseReady && <div className="ai-warning">⏳ {t('db_loading_modal')}</div>}
        {databaseReady && (!allFeatures || allFeatures.length === 0) && <div className="ai-warning">⚠️ {t('no_trails_available')}</div>}
        <div className="ai-modal-content">
          <AIFinder databaseReady={databaseReady} filteredFeatures={filteredFeatures} allFeatures={allFeatures} summaries={summaries} />
        </div>
      </div>
    </div>
  );
}

function WeatherModalMobile({ trail, onClose }) {
  const { t } = useTranslation();
  if (!trail) return null;
  return (
    <div className="weather-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'white', zIndex: 10000, display: 'flex', flexDirection: 'column', animation: 'slideUp 0.3s ease-out' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: 'white', borderBottom: '1px solid #e0e0e0', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', padding: '8px', marginRight: '12px', cursor: 'pointer', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label={t('close')}>←</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{t('weather_and_alerts')}</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>{trail.properties?.name}</p>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <WeatherAlerts coordinates={trail.geometry?.coordinates || []} trailName={trail.properties?.name} trail={trail} />
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </div>
  );
}

function WeatherSidebarDesktop({ trail, onClose }) {
  const { t } = useTranslation();
  if (!trail) return null;
  return (
    <div style={{ position: 'fixed', right: 0, top: '48px', bottom: 0, width: '420px', background: 'white', boxShadow: '-2px 0 10px rgba(0,0,0,0.1)', zIndex: 9999, display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e0e0e0', background: '#f5f5f5' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>{t('weather_and_alerts')}</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>{trail.properties?.name}</p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '8px', minWidth: '44px', minHeight: '44px', color: '#666' }} aria-label={t('close')}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <WeatherAlerts coordinates={trail.geometry?.coordinates || []} trailName={trail.properties?.name} trail={trail} />
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </div>
  );
}

function MobileFilters({ dynamicFilters, selectedDynamic, toggleDynamicFilter, resetFilters, getDifficultyLabel }) {
  const { t } = useTranslation();
  if (!dynamicFilters || Object.keys(dynamicFilters).length === 0) {
    return <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}><p>{t('loading_filters')}</p></div>;
  }
  return (
    <div>
      <div style={{ marginBottom: '16px', textAlign: 'center' }}>
        <button onClick={resetFilters} style={{ padding: '10px 20px', background: '#ff9800', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', minWidth: '44px', minHeight: '44px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>🔄 {t('reset_filters')}</button>
      </div>
      {Object.entries(dynamicFilters).filter(([, values]) => values.length > 1).map(([field, values]) => (
        <div key={field} style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: '600' }}>{getFieldLabel(field, t)}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {values.map((val, index) => {
              const valKey = typeof val === "string" ? val.trim() : String(val);
              const isSelected = selectedDynamic[field]?.has(valKey);
              const label = getDifficultyLabel ? getDifficultyLabel(valKey) : valKey;
              return (
                <button key={`${field}-${index}`} className={`mobile-filter-chip ${isSelected ? 'selected' : ''}`} onClick={() => toggleDynamicFilter(field, valKey)} type="button">
                  <span className="mobile-filter-checkbox">{isSelected && '✓'}</span>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
